import { useState } from 'react';

export default function Projects() {
  const [activeFilter, setActiveFilter] = useState('All');

  const filters = ['All', 'Healthcare', 'Retail', 'Analytics', 'Construction', 'PropTech'];

  const projects = [
    {
      title: 'SureWorx Platform',
      category: 'Construction',
      description: 'Enterprise B2B SaaS platform consolidating seven construction management systems—Health & Safety, Asset Management, QMS, Templates, Documents, Incident Reporting, and Form Builders—into one unified interface for Australian construction organizations.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20enterprise%20construction%20management%20software%20dashboard%20interface%20showing%20multiple%20modules%20and%20data%20visualization%20panels%20with%20clean%20modern%20design%2C%20neutral%20color%20scheme%20with%20teal%20and%20dark%20green%20accents%2C%20minimalist%20professional%20aesthetic%2C%20high%20quality%20digital%20product%20screenshot%2C%20organized%20layout%20with%20clear%20hierarchy%20and%20structured%20information%20display&width=800&height=450&seq=sureworx-dashboard-001&orientation=landscape',
      tags: ['Enterprise UX', 'B2B SaaS', 'Construction'],
      link: '/case-study/sureworx'
    },
    {
      title: 'HOA Management Platform',
      category: 'PropTech',
      description: 'Comprehensive SaaS platform for Home Owners Associations, streamlining community management with integrated payments, maintenance requests, communications, and governance tools for USA and India markets.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20HOA%20homeowners%20association%20management%20dashboard%20interface%20with%20dark%20slate%20blue%20theme%20showing%20community%20overview%20statistics%20resident%20management%20panels%20maintenance%20requests%20and%20financial%20summaries%20clean%20enterprise%20SaaS%20design%20with%20green%20accent%20colors%20professional%20property%20management%20software&width=800&height=450&seq=hoa-project-card-001&orientation=landscape',
      tags: ['Product Design', 'B2B SaaS', 'PropTech'],
      link: '/case-study/hoa-application'
    },
    {
      title: 'Clinic App',
      category: 'Healthcare',
      description: 'Complete patient management system for doctors to manage appointments, patient records, and medical reports. Designed with real doctor feedback to streamline daily clinical workflows and improve patient care efficiency.',
      image: 'https://static.readdy.ai/image/76ac0dfda90dbfd8fe4bf81d25f81847/7d7e2583db7cc8a5822abc5ce6314c70.png',
      tags: ['UX Research', 'Mobile Design', 'Healthcare'],
      link: 'https://www.behance.net/gallery/218229527/My-Clinic'
    },
    {
      title: 'Supermarket App',
      category: 'Retail',
      description: 'Modern e-commerce mobile application for grocery shopping with intuitive product browsing, smart cart management, and seamless checkout experience. Focused on reducing shopping time and improving user satisfaction.',
      image: 'https://static.readdy.ai/image/76ac0dfda90dbfd8fe4bf81d25f81847/cb5ee48c02233c486b0afe9a6535d52b.png',
      tags: ['E-commerce', 'Mobile UX', 'Retail'],
      link: 'https://www.behance.net/gallery/239467545/SmartCart-Smart-Grocery-(UXUI-Case-Study)/modules/1378778137'
    },
    {
      title: 'Power BI Dashboard',
      category: 'Analytics',
      description: 'Comprehensive business intelligence dashboard for healthcare analytics. Transformed complex medical data into clear, actionable insights with interactive visualizations and real-time reporting capabilities.',
      image: 'https://static.readdy.ai/image/76ac0dfda90dbfd8fe4bf81d25f81847/1d39f1e0950468a20f8bf43366ceb5b8.png',
      tags: ['Power BI', 'Data Visualization', 'Analytics'],
      link: 'https://www.behance.net/gallery/239467985/Pharmacy-Product-Insights-Dashboard-(UXUI-Case-Study)/modules/1378781077'
    }
  ];

  const filteredProjects = activeFilter === 'All' 
    ? projects 
    : projects.filter(p => p.category === activeFilter);

  return (
    <section id="projects" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Featured Projects</h2>
          <div className="w-20 h-1 bg-teal-600 mx-auto mb-8"></div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-6 py-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  activeFilter === filter
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {filteredProjects.map((project, index) => {
            const CardContent = (
              <>
                <div className="aspect-video overflow-hidden bg-gray-100">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="px-3 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full whitespace-nowrap">
                      {project.category}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{project.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            );

            return project.link ? (
              <a 
                key={index}
                href={project.link}
                target={project.link.startsWith('http') ? '_blank' : undefined}
                rel={project.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 cursor-pointer block"
                data-product-shop
              >
                {CardContent}
              </a>
            ) : (
              <div 
                key={index}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 cursor-pointer"
                data-product-shop
              >
                {CardContent}
              </div>
            );
          })}

          {/* Behance Portfolio Card */}
          <a 
            href="https://www.behance.net/ranga_uidesigner"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2 cursor-pointer block p-8 flex flex-col items-center justify-center text-center min-h-[400px]"
          >
            <div className="w-20 h-20 flex items-center justify-center mb-6">
              <i className="ri-behance-fill text-white text-6xl"></i>
            </div>
            <h3 className="text-3xl font-bold text-white mb-4">View Full Portfolio</h3>
            <p className="text-white/90 text-lg mb-6 max-w-md">
              Explore more case studies and design work on Behance
            </p>
            <div className="flex items-center gap-2 text-white font-medium">
              <span>Visit Behance</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}